/* @flow */

import React from 'react';
import * as d3 from 'd3';
import ReactFauxDOM from 'react-faux-dom';
import cx from 'classnames';
import _ from 'lodash';

import './LineGraph.css';

// const log = require('bows')('LineGraph');

type LabelsType = {
  'y1.legned': string,
  'y2.legend': string,
  x: string,
  y1: string,
  y2: string,
};

type DataPointType = { id: number, key: string, val: number };

type MarkerObjectType = {
  'Total Module Loss'?: number,
  IOverload?: number,
  Irated?: number,
  MaxTjDh?: number,
  MaxTjigbt?: number,
  MeanTjDh?: number,
  MeanTjigbt?: number,
};

type PropsType = {
  data: { [key: string]: [] },
  height: number,
  labels: LabelsType,
  markers: MarkerObjectType,
  scaleX: Function,
  viewBox: ViewBox,
  width: number,
};

const FIELD_DOMAIN = [
  'FWD Loss (W)',
  'Hi-Side Modulation Signal',
  'FWD Tj (°C)',
  'Hi-Side IGBT/MOSFET Current (A)',
  'Total Module Loss (W)',
  'Thermal Resistance Limited by Ths (°C/W)',
  'Max Allowable Case Temperature (°C)',
  'IGBT/MOSFET Tj (°C)',
  'Thermal Resistance Limited by Tj (°C/W)',
  'IGBT/MOSFET Loss (W)',
  'IGBT ΔTj-c',
  'FWD ΔTj-c',
  'IGBT/MOSFET Loss (W)',
  'IGBT/MOSFET Tj (°C)',
];

const maxMarkerFields = ['MaxTjDh', 'Max Tj', 'MaxTjigbt', 'Max Tj-D'];
const meanMarkerFields = ['MeanTjDh', 'Mean Tj', 'MeanTjigbt', 'Mean Tj-D'];

// calculate the width of the text
const calculateTextWidth = (keys: string[] | string, textSize: number = 12): number => {
  const canvas = document.createElement('canvas');

  const ctx = canvas.getContext('2d');

  if (ctx) {
    ctx.font = `${textSize}px Arial`;

    if (typeof keys === 'string') {
      return ctx.measureText(keys).width;
    }

    return keys.reduce(
      (ret: number, str: string): number => {
        const len = ctx.measureText(str).width;
        if (len > ret) return len;
        return ret;
      },
      0,
    );
  }
  return 0;
};

export class LineGraph extends React.PureComponent<PropsType, *> {
  props: PropsType; // eslint-disable-line react/sort-comp

  render(): ?React$Element<*> {
    const { data: orgData, viewBox, width, height, labels, markers } = this.props;

    // log(this.props);

    const data = _.omit(orgData, 'Unit');

    if (!_.size(data)) return null;

    const xAxisData = orgData.Unit;
    const y1AxisData = data[labels.y1];
    const y2AxisData = data[labels.y2];

    const thisData: Array<Array<DataPointType>> = _.map(
      data,
      (values: number[], key: string): Object[] => {
        let vals = values;
        // this is dumb.. some data is not the same length.
        if (xAxisData.length < values.length) {
          vals = values.slice(0, xAxisData.length);
        }
        return vals.map((value, index) => ({ id: key, key: xAxisData[index], val: value }));
      },
    );

    // Fatten the [][]
    const flatData = _.flatten(thisData);
    // Set up the base SVG
    const svg: Element = ReactFauxDOM.createElement('svg');

    const svgWrapper: any = d3.select(svg)
      .attr('class', 'graph')
      .attr('viewBox', `${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height + 50}`)
      .attr('width', width)
      .attr('height', height)
      // .attr('data', null)
      // .append('g');
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .attr('transform', 'translate(0, 0)');

    const el = svgWrapper.append('g')
      .attr('class', 'groupWrapper');

    // set a container with margins
    const group = el.append('g')
      .attr('transform', 'translate(0, 5)');

    const x = d3.scaleLinear()
      .range([0, viewBox.width])
      .domain(d3.extent(flatData, d => d.key))
      .clamp(true);
      // .nice();

    const y1 = d3.scaleLinear()
      .range([viewBox.height, d3.min(flatData, v => v.val)])
      // .domain(d3.extent(flatData, d => d.val))
      .domain(d3.extent(y1AxisData))
      .clamp(true)
      .nice();

    const y2 = d3.scaleLinear()
      .range([viewBox.height, d3.min(flatData, v => v.val)])
      // .domain(d3.extent(flatData, d => d.val))
      .domain(d3.extent(y2AxisData))
      .clamp(true)
      .nice();

    const color = d3.scaleOrdinal(d3.schemeCategory20)
      .domain(FIELD_DOMAIN);

    const getColor = (key: string) => {
      // console.log(FIELD_DOMAIN.includes(key) ? 'key' : 'missing', key);
      if (['Hi-Side IGBT/MOSFET Current (A)', 'Thermal Resistance Limited by Ths (°C/W)'].includes(key)) {
        return 'blue';
      }

      if (['Hi-Side Modulation Signal', 'Thermal Resistance Limited by Tj (°C/W)'].includes(key)) {
        return 'green';
      }

      return color(key);
    };

    const y1Line = d3.line()
      .curve(d3.curveMonotoneX)
      .x(d => x(d.key))
      .y(d => y1(d.val));

    const y2Line = d3.line()
      .curve(d3.curveMonotoneX)
      .x(d => x(d.key))
      .y(d => y2(d.val));

    const getMarkerLines = (obj: any): { lines: Array<*> } => {
      let lines = [];

      // log('test');

      if (!obj) return { lines };

      const lineMarkers = _.values(obj);

      if (lineMarkers[0] && lineMarkers[1]) {
        lines = [
          d3.line().x(d => x(d.key)).y(() => y2(lineMarkers[1])),
          d3.line().x(d => x(d.key)).y(() => y2(lineMarkers[0])),
        ];
      }

      const totalModuleLoss = obj['Total Module Loss'];
      if (totalModuleLoss) {
        lines = [
          d3.line().x(d => x(d.key)).y(() => y1(totalModuleLoss)),
        ];
      }

      // log(obj);

      const iOverload = obj['I-Overload'];
      const iRated = obj['I-rated'];
      if (iOverload && iRated) {
        const niceX = x.nice();
        lines = [
          d3.line().x(() => niceX(iOverload)).y(d => y2(d.val)),
          d3.line().x(() => niceX(iRated)).y(d => y2(d.val)),
        ];
      }

      return {
        lines,
      };
    };

    const markerLines = getMarkerLines(markers);

    // Create axis
    const xAxis = d3.axisBottom(x);
    const yAxis = d3.axisLeft(y1);
    const y1Axis = d3.axisRight(y2);

    // Create x-axis
    group.append('g')
      .attr('class', 'axis')
      .attr('transform', `translate(0, ${viewBox.height})`)
      .call(xAxis);

    // Add x-axis label
    group.append('g')
      .attr('transform', `translate(${viewBox.width / 2}, ${viewBox.height + 35})`)
      .append('text')
      .attr('class', 'x label')
      .style('font-size', '12px')
      .style('font-weight', 'bold')
      .attr('text-anchor', 'middle')
      .text(labels.x);

    // Create y-axis
    group.append('g')
      .attr('class', 'axis')
      .call(yAxis);

    group.append('g')
      .attr('class', 'axis')
      .attr('transform', `translate(${viewBox.width}, 0)`)
      .call(y1Axis);

    // Add y-axis label
    group.append('g')
      .attr('transform', `translate(${-40}, ${viewBox.height / 2})`)
      .append('text')
      .attr('class', 'y label')
      .style('font-size', '12px')
      .style('font-weight', 'bold')
      .attr('text-anchor', 'middle')
      .attr('transform', 'rotate(-90)')
      .text(labels.y1);

    group.append('g')
      .attr('transform', `translate(${viewBox.width + 40}, ${viewBox.height / 2})`)
      .append('text')
      .attr('class', 'y label')
      .style('font-size', '12px')
      .style('font-weight', 'bold')
      .attr('text-anchor', 'middle')
      .attr('transform', 'rotate(90)')
      .text(labels.y2);

    // Create the grid
    group.append('g')
      .attr('class', 'grid')
      .attr('transform', `translate(0, ${viewBox.height})`)
      .call(xAxis.tickSize(-viewBox.height, 0, 0).tickFormat(''));

    group.append('g')
      .attr('class', 'grid')
      .attr('transform', 'translate(0, 0)')
      .call(yAxis.tickSize(-viewBox.width, 0, 0).tickFormat(''));

    // Add legend
    const legendSize = 12;
    const legendSpacing = 5;
    const textSize = 12;

    const legend = el.append('g')
      .attr('class', 'legend')
      .attr('transform', 'translate(0, 0)');

    // make the legend more box like
    legend.append('rect')
      .attr('class', 'legendBackground')
      .style('stroke', 'black')
      .style('stroke-width', 0.5);

    const legendRow = legend.append('g')
      .attr('transform', `translate(${legendSpacing}, ${legendSpacing})`);

    // Append all of our lines and legend text
    let count = 0;

    const pathGroup = group.append('g');
    _.forEach(thisData,
      (value: Array<DataPointType>) => {
        const legendKey = _.get(value[0], ['id'], 'unknown');
        const row = legendRow.append('g')
          .attr('class', 'legendRow');

        // Legend rectangle
        row.append('rect')
          .attr('width', legendSize)
          .attr('height', legendSize)
          .attr('x', 0)
          .attr('y', count * (legendSize + legendSpacing))
          .style('fill', getColor(legendKey));

        // Legend text
        const legendText = row.append('text')
          .attr('x', legendSize + legendSpacing)
          .attr('y', count * (legendSize + legendSpacing))
          .attr('dy', legendSize)
          .style('fill', 'black')
          .attr('font-size', textSize)
          .attr('transform', 'translate(0, -2)');

        count += 1;

        if (legendKey === labels.y1) {
          pathGroup.append('path')
            .data([value])
            .attr('class', cx('line'))
            .attr('d', y1Line)
            .style('stroke', getColor(legendKey));

          legendText.text(_.get(labels, ['y1.legend'], legendKey));
        } else {
          pathGroup.append('path')
            .data([value])
            .attr('class', cx('line'))
            .attr('d', y2Line)
            .style('stroke', getColor(legendKey));

          legendText.text(_.get(labels, ['y2.legned'], legendKey));
        }

        markerLines.lines.forEach((line: []) => pathGroup
          .append('path')
          .data([value])
          .attr('class', cx('line'))
          .attr('d', line)
          .style('stroke', 'darkblue')
          .style('stroke-dasharray', '3, 5')
          .style('stroke-width', 0.75),
        );
      });

    _.forEach(markers, (val: number, key: string) => {
      if (key === 'fs') return;

      if (['Total Module Loss', ...maxMarkerFields, ...meanMarkerFields].includes(key)) {
        group.append('text')
          .attr('x', viewBox.width - 20)
          .attr('y', key === 'Total Module Loss' ? y1(val) : y2(val))
          .style('fill', 'black')
          .style('font-size', '12px')
          .attr('text-anchor', 'right')
          .attr('transform', `translate(-${calculateTextWidth(key)}, 12)`)
          .text(key);
      } else {
        group.append('text')
          .attr('y', -x(val))
          .attr('x', 0)
          .style('fill', 'black')
          .style('font-size', '12px')
          .attr('text-anchor', 'left')
          .attr('transform', 'translate(-12, 20) rotate(90)')
          .text(key);
      }
    });

    // reattach for zindex order
    legend.raise();

    // const textWidth = calculateTextWidth(Object.keys(data), textSize);
    legend.select('.legendBackground')
      .attr('height', legendSpacing + (count * (legendSize + legendSpacing)))
      .attr('fill', 'white');
    // .attr('width', ((textWidth) + (legendSize + (legendSpacing * 3))));

    // Convert the d3 node to a React node
    return svgWrapper.node().toReact();
  }
}

export default LineGraph;



// WEBPACK FOOTER //
// ./src/components/LineGraph/LineGraph.js